import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CRM() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', status: 'lead' });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const { data } = await axios.get(`${API}/crm/companies`, { withCredentials: true });
      setCompanies(data);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/crm/companies`, formData, { withCredentials: true });
      toast.success('Company created successfully');
      setShowDialog(false);
      setFormData({ name: '', status: 'lead' });
      loadCompanies();
    } catch (error) {
      toast.error('Failed to create company');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await axios.delete(`${API}/crm/companies/${id}`, { withCredentials: true });
      toast.success('Company deleted');
      loadCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const statusColors = {
    lead: 'bg-blue-100 text-blue-800 border-blue-200',
    prospect: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    client: 'bg-green-100 text-green-800 border-green-200',
    inactive: 'bg-zinc-100 text-zinc-800 border-zinc-200'
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="crm-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="crm-title">
            CRM
          </h1>
          <p className="mt-2 text-zinc-600">Manage your client relationships</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-company-button">
              <Plus size={18} /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Add New Company</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Company Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="company-name-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="status" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger className="mt-2" data-testid="company-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-company-button">
                Create Company
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="companies-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Company Name</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Created</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {companies.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-zinc-500" data-testid="no-companies">
                    No companies yet. Add your first company to get started.
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id} className="hover:bg-zinc-50 transition-colors" data-testid={`company-row-${company.id}`}>
                    <td className="px-6 py-4 font-medium text-zinc-900">{company.name}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold border ${statusColors[company.status]}`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">
                      {new Date(company.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(company.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        data-testid={`delete-company-${company.id}`}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
